import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { Field, formValueSelector, reduxForm } from 'redux-form';

import type { CrewTypeViewModel } from 'ab-viewModels/crewType.viewModel';
import type { EmployeeOptionVM } from 'ab-viewModels/employee/option.viewModel';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import { formatDate, parseDate } from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import { WorkOrderStatus } from '@acceligentllc/shared/enums/workOrderStatus';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import { WORK_ORDER_FORM } from 'af-constants/reduxForms';

import Dropdown from 'af-fields/Dropdown';
import Input from 'af-fields/Input';

import PlainDateInput from 'af-components/Controls/DateInput';
import DropdownEmployeeItem from 'af-components/DropdownEmployeeItem';
import LabelWithColor from 'af-components/LabelWithColor';

import * as CrewTypeActions from 'af-actions/crewType';
import * as EmployeeActions from 'af-actions/employee';

import type WorkOrderFM from '../formModel';
import styles from './styles.module.scss';

interface OwnProps {
	onDateChange: (date: string) => void;
	resetTimer: () => void;
}

type Props = OwnProps & ConnectedProps<typeof connector> & InjectedFormProps<WorkOrderFM>;

interface State {
	selectedDate: Nullable<Date>;
	crewTypes: CrewTypeViewModel[];
	superintendents: EmployeeOptionVM[];
}

class Details extends React.PureComponent<Props, State> {
	_datepicker = null;

	state: State = {
		selectedDate: this.props.dueDate ? parseDate(this.props.dueDate, TimeFormat.DB_DATE_ONLY) : null,
		crewTypes: [],
		superintendents: [],
	};

	static FILTER_SI_BY = ['firstName', 'lastName', 'fullName', 'uniqueId', 'accountTemplate'];

	static getDerivedStateFromProps(props: Props) {
		return {
			selectedDate: props.dueDate ? parseDate(props.dueDate, TimeFormat.DB_DATE_ONLY) : null,
		};
	}

	static renderSuperintendent = (option: EmployeeOptionVM) => <DropdownEmployeeItem option={option} showUserRole={true} />;
	static renderSuperintendentSelected = (option: EmployeeOptionVM) => <DropdownEmployeeItem option={option} showUserRole={false} />;

	renderCrewTypeOption = (option: CrewTypeViewModel) => {
		const { canManageForeman } = this.props;
		return (
			<div className={styles['crew-option']}>
				{
					canManageForeman && option.foremanEnabled &&
					<div className={`${styles['foreman-icon']}`} >
						<span className="icon-foreman" />
					</ div>
				}
				<LabelWithColor color={option.color} text={option.name} />
			</div>
		);
	};

	componentDidUpdate(prevProps: Props) {
		if (prevProps.crewTypeId !== this.props.crewTypeId || prevProps.customCrewType !== this.props.customCrewType) {
			this.updateInternalState();
		}

		if (this.props.crewNumber && !this.props.crewType?.foremanEnabled) {
			this.props.change('crewNumber', null);
		}
	}

	onCrewTypeChange = () => {
		const { change, resetTimer } = this.props;

		change('crewNumber', null);
		resetTimer();
	};

	onDateSelect = async (date: Date) => {
		const { resetTimer, onDateChange, change } = this.props;

		const selectedDate = formatDate(date, TimeFormat.DB_DATE_ONLY);
		onDateChange(selectedDate);
		change('dueDate', selectedDate);
		resetTimer();
	};

	updateInternalState = () => {
		const { isJobInternal, isWOInternal, crewTypeId, customCrewType, change } = this.props;
		const isInternal = (isJobInternal && !!customCrewType) || (!isJobInternal && !crewTypeId);
		if (isWOInternal !== isInternal) {
			change('isInternal', isInternal);
		}
	};

	lazyLoadSuperintendent = async (isLazyLoaded: boolean) => {
		const { loadSuperintendents } = this.props;
		if (!isLazyLoaded) {
			const superintendents = await loadSuperintendents();
			this.setState(() => ({ superintendents }));
		}
	};

	lazyLoadCrewType = async (isLazyLoaded: boolean) => {
		const { loadCrewTypes } = this.props;
		if (!isLazyLoaded) {
			const crewTypes = await loadCrewTypes();
			this.setState(() => ({ crewTypes }));
		}
	};

	render() {
		const {
			disabled,
			resetTimer,
			isJobInternal,
			dueDate,
			workOrderId,
			crewType,
			canManageForeman,
		} = this.props;
		const { selectedDate, crewTypes, superintendents } = this.state;
		return (
			<Row className="row--padded work-order-upsert__section">
				<Col lg={6}>
					{isJobInternal ?
						<Field
							component={Input}
							disabled={disabled}
							id="customCrewType"
							isRequired={true}
							isStandalone={true}
							label="Crew Type"
							name="customCrewType"
							placeholder="Enter Crew Type"
						/> :
						<Field
							component={Dropdown}
							containerId="work-order__crew-type"
							disabled={disabled}
							disableErrorMessages={true}
							dropdownId="work-order__crew-type--dropdown"
							id="crewTypeId"
							isWhite={true}
							label="Crew Type *"
							name="crewTypeId"
							onLazyLoad={this.lazyLoadCrewType}
							onValueChange={this.onCrewTypeChange}
							options={crewTypes}
							placeholder="Select Crew Type"
							propName="crewType"
							renderMenuItem={this.renderCrewTypeOption}
							renderSelected={this.renderCrewTypeOption}
							valueKey="id"
							withCaret={true}
						/>
					}
					{crewType?.foremanEnabled && canManageForeman &&
						<Field
							component={Input}
							disabled={disabled}
							id="crewNumber"
							isStandalone={true}
							label="Crew #"
							name="crewNumber"
							onValueChange={resetTimer}
							placeholder="Enter Crew #"
							type="text"
						/>
					}
				</Col>
				<Col lg={6}>
					<Field
						component={Dropdown}
						containerId="work-order__superintendent"
						disabled={disabled}
						dropdownClassName="work-order-upsert__user-type-dropdown"
						dropdownId="work-order__superintendent--dropdown"
						filterable={true}
						filterBy={Details.FILTER_SI_BY}
						id="supervisorId"
						label="Superintendent *"
						name="supervisorId"
						onLazyLoad={this.lazyLoadSuperintendent}
						onValueChange={resetTimer}
						options={superintendents}
						placeholder="Select Superintendent"
						propName="supervisor"
						renderMenuItem={Details.renderSuperintendent}
						renderSelected={Details.renderSuperintendentSelected}
						valueKey="id"
						withCaret={true}
					/>
				</Col>
				{dueDate && workOrderId ?
					<Col lg={12}>
						<div>Date *</div>
						<div className="work-order-upsert__sticky-date-locked" >
							{formatDate(dueDate, TimeFormat.DAY_WITH_DATE)}
						</div>
					</Col> :
					<>
						<Col lg={4}>
							<PlainDateInput
								dateFormat={TimeFormat.DATE_ONLY}
								defaultValue={selectedDate ?? undefined}
								disabled={disabled}
								isStandalone={true}
								label="Date *"
								onValueChange={this.onDateSelect}
								originalDateFormat={TimeFormat.DB_DATE_ONLY}
								placeholderText="Select Date of Work *"
								selectedValue={selectedDate ?? undefined}
							/>
						</Col>
						<Col lg={8} />
					</>
				}
			</Row>
		);
	}
}

const selector = formValueSelector(WORK_ORDER_FORM);

function mapStateToProps(state: RootState) {

	const { workOrder: { order }, user: { companyData, userData } } = state;

	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const { isCompanyAdmin, permissions } = companyData;
	const { role } = userData;

	const canManageForeman = isAllowed(
		PagePermissions.COMPANY.FOREMAN.MANAGE,
		permissions,
		isCompanyAdmin,
		role
	);

	const {
		id: workOrderId,
		job,
		dueDate,
		status,
		isInternal: isWOInternal,
		customCrewType,
		crewTypeId,
		crewType,
		crewNumber,
	} = selector(
		state,
		'id',
		'job',
		'dueDate',
		'status',
		'isInternal',
		'customCrewType',
		'crewTypeId',
		'crewType',
		'crewNumber'
	);

	return {
		workOrderId,
		disabled: status === WorkOrderStatus.CANCELED || status === WorkOrderStatus.LOCKED || order?.isPaused,
		isJobInternal: job?.isInternal,
		isWOInternal,
		dueDate,
		customCrewType,
		crewTypeId,
		crewType,
		crewNumber,
		canManageForeman,
	};
}

function mapDispatchToProps() {
	return {
		loadSuperintendents: EmployeeActions.findAllSuperintendents,
		loadCrewTypes: CrewTypeActions.findAllForCompanyList,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	connector,
	reduxForm<WorkOrderFM>({ form: WORK_ORDER_FORM })
);

export default enhance(Details);
